import { FunctionComponent, useContext, useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

import { Color, Player } from "./Types";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { GameState } from "./store/gameStateSlice";
import { ActionsContext, LocalGameActions } from "./ActionsContext";
import Checker, { CheckerStatus } from "./Checker";
import { useNavigate } from "react-router-dom";
import { setWipeTransition } from "./store/wipeTransitionSlice";
import { resetLocalStore } from "./Utils";
import { calculateElo, calculateWinnerRewards, calculateLoserRewards } from "./services/EloService";
import { applyGameResult, expForNextLevel } from "./services/FirebaseUsers";
import { settleMatchOnServer } from "./services/MatchSettleService";
import { updateProfileField } from "./store/userSlice";
import { addToast } from "./store/toastSlice";
import { ALL_ACHIEVEMENTS } from "./AchievementsPanel";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getDB } from "./Firebase";

type GameOverDialogProps = {
  playerPerspective: Player;
};

interface GameResult {
  ratingDelta: number;
  coinsGained: number;
  expGained: number;
  newLevel: number;
  leveledUp: boolean;
  newAchievements: string[];
}

const GameOverDialog: FunctionComponent<GameOverDialogProps> = ({
  playerPerspective,
}) => {
  const [
    gameState, settings, currentPlayer, matchScore,
    readyForNextGameData, myProfile, players, bet,
  ] = useAppSelector((state) => [
    state.gameState,
    state.settings,
    state.currentPlayer,
    state.matchScore,
    state.readyForNextGame,
    state.user.profile,
    state.players,
    state.bet,
  ]);
  const actions = useContext(ActionsContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const processedRef = useRef(false);
  const [result, setResult] = useState<GameResult | null>(null);

  const isGammon     = gameState === GameState.GameOverGammon;
  const isBackgammon = gameState === GameState.GameOverBackgammon;
  const isForfeit    = gameState === GameState.GameOverForfeit;
  const isLocalGame  = actions instanceof LocalGameActions;

  // При сдаче (forfeit): currentPlayer — тот, кто кликнул «Сдаться» (ходил → проиграл).
  // При обычной победе: currentPlayer — победитель.
  const isWinner = isLocalGame
    ? true
    : isForfeit
      ? currentPlayer !== playerPerspective   // кто сдался — тот проиграл
      : currentPlayer === playerPerspective;

  // Слот реального победителя нужен для проверки очков матча
  const winnerSlot: Player = isForfeit
    ? (currentPlayer === Player.One ? Player.Two : Player.One)
    : currentPlayer;
  const isMatchWin = matchScore[winnerSlot] >= matchScore.pointsRequiredToWin;

  // ── Обработка результатов ────────────────────────────────────────────────
  useEffect(() => {
    if (!settings.showGameOverDialog || processedRef.current || isLocalGame || !myProfile) return;
    processedRef.current = true;

    const opponentKey = playerPerspective === Player.One ? "playerTwo" : "playerOne";
    const opponentRating = players[opponentKey]?.rating ?? 1000;
    const opponent = players[opponentKey];

    const eloResult = calculateElo(
      isWinner ? myProfile.rating : opponentRating,
      isWinner ? opponentRating  : myProfile.rating,
      isGammon, isBackgammon
    );
    const myDelta    = isWinner ? eloResult.winnerDelta : eloResult.loserDelta;
    // Расчёт coinsGained на клиенте оставлен только для отображения в диалоге.
    // Реальное списание/начисление делает сервер /api/match/settle (см. ниже).
    const rewards    = isWinner
      ? calculateWinnerRewards(isGammon, isBackgammon, isMatchWin, bet, myProfile.winStreak)
      : calculateLoserRewards(bet);

    // Проверяем достижения
    const prevAchs = new Set(myProfile.achievements ?? []);
    const updatedProfile = {
      ...myProfile,
      rating:        Math.max(100, myProfile.rating + myDelta),
      coins:         Math.max(0, myProfile.coins + rewards.coinsGained),
      experience:    myProfile.experience + rewards.expGained,
      gamesPlayed:   myProfile.gamesPlayed + 1,
      wins:          isWinner ? myProfile.wins + 1 : myProfile.wins,
      losses:        isWinner ? myProfile.losses : myProfile.losses + 1,
      winStreak:     isWinner ? myProfile.winStreak + 1 : 0,
      longestWinStreak: isWinner
        ? Math.max(myProfile.longestWinStreak, myProfile.winStreak + 1)
        : myProfile.longestWinStreak,
      gammonsWon:    myProfile.gammonsWon + (isWinner && isGammon ? 1 : 0),
      backgammonsWon: myProfile.backgammonsWon + (isWinner && isBackgammon ? 1 : 0),
    };

    const newAchs = ALL_ACHIEVEMENTS
      .filter((a) => !prevAchs.has(a.id) && a.check(updatedProfile))
      .map((a) => a.id);

    // Level up
    const expNeeded   = expForNextLevel(myProfile.level);
    const newExp      = myProfile.experience + rewards.expGained;
    const leveledUp   = newExp >= expNeeded;
    const newLevel    = leveledUp ? myProfile.level + 1 : myProfile.level;

    const gameResult: GameResult = {
      ratingDelta:     myDelta,
      coinsGained:     rewards.coinsGained,
      expGained:       rewards.expGained,
      newLevel,
      leveledUp,
      newAchievements: newAchs,
    };
    setResult(gameResult);

    // Обновляем Redux: coins пересчитаем после ответа сервера.
    dispatch(updateProfileField({
      rating:            updatedProfile.rating,
      experience:        newExp >= expNeeded ? newExp - expNeeded : newExp,
      level:             newLevel,
      gamesPlayed:       updatedProfile.gamesPlayed,
      wins:              updatedProfile.wins,
      losses:            updatedProfile.losses,
      winStreak:         updatedProfile.winStreak,
      longestWinStreak:  updatedProfile.longestWinStreak,
      gammonsWon:        updatedProfile.gammonsWon,
      backgammonsWon:    updatedProfile.backgammonsWon,
      achievements:      [...Array.from(prevAchs), ...newAchs],
    }));

    // Серверный settle (единственный источник правды для денег).
    // matchId детерминирован для обоих клиентов в этой партии,
    // сервер идемпотентен: повторный вызов с тем же id ничего не делает.
    const matchScoreBeforeWin = matchScore; // ещё не успели применить newScore
    const matchSettleId = actions.getCurrentMatchSettleId(matchScoreBeforeWin);
    const winnerUid = isWinner ? myProfile.uid : (opponent?.uid ?? null);
    const loserUid  = isWinner ? (opponent?.uid ?? null) : myProfile.uid;

    if (matchSettleId && winnerUid && loserUid && bet && bet > 0) {
      // Серверный settle — атомарный, идемпотентный.
      // После ответа обновляем баланс в Redux из реального ответа сервера.
      settleMatchOnServer({
        matchId:      matchSettleId,
        winnerUid,
        loserUid,
        bet,
        isGammon,
        isBackgammon,
        isMatchWin,
      })
        .then((res) => {
          if (!res || !res.ok) {
            // Сервер вернул ошибку — применяем клиентский расчёт как fallback
            dispatch(updateProfileField({ coins: Math.max(0, updatedProfile.coins) }));
            return;
          }
          const delta = res.yourDelta;
          dispatch(updateProfileField({
            coins: Math.max(0, (myProfile.coins || 0) + delta),
          }));
          if (delta !== 0) {
            dispatch(addToast({
              type: "coins",
              message: delta > 0 ? "Заработано" : "Списано",
              amount: Math.abs(delta),
            }));
          }
        })
        .catch(() => {
          // Сеть недоступна — применяем клиентский расчёт
          dispatch(updateProfileField({ coins: Math.max(0, updatedProfile.coins) }));
        });
    } else {
      // Нет ставки (bet=0) или нет matchSettleId — просто показываем итог без денег
      dispatch(updateProfileField({
        coins: Math.max(0, updatedProfile.coins),
      }));
    }

    applyGameResult(myProfile.uid, {
      isWinner, isGammon, isBackgammon,
      ratingDelta:     myDelta,
      expGained:       rewards.expGained,
      coinsGained:     rewards.coinsGained,
      newAchievements: newAchs,
    }, { skipCoins: !!matchSettleId });

    // Сохраняем историю игры (пишет только победитель, чтобы не дублировать)
    if (isWinner && !isLocalGame) {
      addDoc(collection(getDB(), "game_history"), {
        winnerId:      myProfile.uid,
        winnerNick:    myProfile.nickname,
        winnerRating:  myProfile.rating,
        loserId:       opponent?.uid   ?? null,
        loserNick:     opponent?.nickname ?? null,
        loserRating:   opponent?.rating   ?? null,
        betAmount:     bet ?? 0,
        isGammon, isBackgammon, isForfeit,
        isMatchWin,
        ratingDelta:   myDelta,
        endedAt:       serverTimestamp(),
      }).catch(() => {});
    }

    // Тост про монеты теперь идёт после ответа сервера (см. settleMatchOnServer выше).
    // Здесь оставляем только level-up и достижения.
    if (leveledUp) {
      dispatch(addToast({ type: "levelup", message: `Уровень ${newLevel}!`, subtext: "Вы повысили уровень" }));
    }
    newAchs.forEach((id) => {
      const def = ALL_ACHIEVEMENTS.find((a) => a.id === id);
      if (def) dispatch(addToast({ type: "achievement", message: def.name, subtext: def.description }));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.showGameOverDialog]);

  const handleNextGame = () => {
    processedRef.current = false;
    setResult(null);
    if (actions.isHostClient()) {
      actions.nextGameButtonClicked({ ...readyForNextGameData, [Player.One]: true });
    } else {
      actions.nextGameButtonClicked({ ...readyForNextGameData, [Player.Two]: true });
    }
  };

  const handleMenu = () => {
    processedRef.current = false;
    setResult(null);
    resetLocalStore(dispatch, true);
    dispatch(setWipeTransition(true));
    navigate("/");
  };

  // ── Тексты ──────────────────────────────────────────────────────────────
  let winnerText = isLocalGame
    ? (currentPlayer === Player.One ? "Первый игрок выиграл" : "Второй игрок выиграл")
    : (isWinner ? "Победа!" : "Поражение");
  winnerText += isMatchWin ? " Матч!" : "";

  const isLongNardy = settings.gameMode === "LONG_NARDY";
  let subText: string | null = null;
  // В длинных нардах нет гаммона/бэкгаммона
  if (!isLongNardy && isBackgammon) subText = "Бэкгаммон — очки ×3";
  else if (!isLongNardy && isGammon)  subText = "Гаммон — очки ×2";
  else if (isForfeit) subText = isWinner ? "Соперник сдался" : "Вы сдались";

  const headerText  = isMatchWin ? "Матч окончен" : "Игра окончена";
  let winnerColor = Color.White;
  if (currentPlayer === Player.One) {
    winnerColor = settings.playerOneColor;
  } else {
    winnerColor = settings.playerOneColor === Color.White ? Color.Black : Color.White;
  }

  const myProfile_ = myProfile;
  const expNeeded  = myProfile_ ? expForNextLevel(result?.newLevel ?? myProfile_.level) : 100;
  const expCurrent = myProfile_
    ? (result?.leveledUp
        ? (myProfile_.experience + (result?.expGained ?? 0)) - expForNextLevel(myProfile_.level)
        : myProfile_.experience + (result?.expGained ?? 0))
    : 0;
  const expPercent = Math.min(100, (expCurrent / expNeeded) * 100);

  return (
    <Modal
      show={settings.showGameOverDialog}
      backdrop="static"
      centered
      dialogClassName={`Game-over-dialog ${isWinner ? "winner" : "loser"}`}
    >
      <Modal.Header>
        <Modal.Title>{headerText}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Победитель */}
        <div className="GO-result-row">
          <div className="GO-checker-wrap">
            <Checker color={winnerColor} checkerPulse={false}
              status={CheckerStatus.None} onAnimationComplete={() => {}} animation={null} />
          </div>
          <div className="GO-texts">
            <div className={`GO-winner-text ${isWinner ? "win" : "lose"}`}>
              {winnerText}
            </div>
            {subText && <div className="GO-sub-text">{subText}</div>}
          </div>
          <div className="GO-checker-wrap">
            <Checker color={winnerColor} checkerPulse={false}
              status={CheckerStatus.None} onAnimationComplete={() => {}} animation={null} />
          </div>
        </div>

        {/* Ставка — показываем только релевантное для каждой стороны */}
        {!isLocalGame && bet > 0 && (
          <div className="GO-bet-summary">
            <span className="GO-bet-summary-label">Банк</span>
            <span className="GO-bet-summary-pot">{bet * 2} ₽</span>
            <span className="GO-bet-summary-sep">·</span>
            {isWinner ? (
              <>
                <span className="GO-bet-summary-label">Комиссия 20%</span>
                <span className="GO-bet-summary-commission">−{Math.floor(bet * 2 * 0.2)} ₽</span>
                <span className="GO-bet-summary-sep">·</span>
                <span className="GO-bet-summary-label">Выигрыш</span>
                <span className="GO-bet-summary-result win">+{Math.floor(bet * 2 * 0.8)} ₽</span>
              </>
            ) : (
              <>
                <span className="GO-bet-summary-label">Потеря</span>
                <span className="GO-bet-summary-result lose">−{bet} ₽</span>
              </>
            )}
          </div>
        )}

        {/* Результаты для онлайн-игры */}
        {!isLocalGame && result && (
          <div className="GO-stats">
            <div className={`GO-stat-item rating ${result.ratingDelta >= 0 ? "pos" : "neg"}`}>
              <span className="GO-stat-label">Рейтинг</span>
              <span className="GO-stat-value">
                {result.ratingDelta >= 0 ? "+" : ""}{result.ratingDelta}
              </span>
            </div>
            <div className={`GO-stat-item coins ${result.coinsGained >= 0 ? "" : "neg"}`}>
              <span className="GO-stat-label">Монеты</span>
              <span className="GO-stat-value">
                {result.coinsGained >= 0 ? "+" : ""}{result.coinsGained} ₽
              </span>
            </div>
            <div className="GO-stat-item xp">
              <span className="GO-stat-label">Опыт</span>
              <span className="GO-stat-value">+{result.expGained} XP</span>
            </div>
          </div>
        )}

        {/* XP-бар и уровень */}
        {!isLocalGame && myProfile_ && (
          <div className="GO-level-section">
            <div className="GO-level-row">
              <span className="GO-level-label">Уровень {result?.newLevel ?? myProfile_.level}</span>
              {result?.leveledUp && (
                <span className="GO-levelup-badge">Новый уровень!</span>
              )}
              <span className="GO-level-exp">{Math.round(expPercent)}%</span>
            </div>
            <div className="GO-exp-bar">
              <div className="GO-exp-fill" style={{ width: `${expPercent}%` }} />
            </div>
          </div>
        )}

        {/* Новые достижения */}
        {result && result.newAchievements.length > 0 && (
          <div className="GO-achievements">
            <div className="GO-ach-title">Новые достижения</div>
            {result.newAchievements.map((id) => {
              const def = ALL_ACHIEVEMENTS.find((a) => a.id === id);
              if (!def) return null;
              return (
                <div key={id} className="GO-ach-item">
                  <span className="GO-ach-icon">{def.icon}</span>
                  <div className="GO-ach-info">
                    <span className="GO-ach-name">{def.name}</span>
                    <span className="GO-ach-desc">{def.description}</span>
                  </div>
                  {def.reward > 0 && (
                    <span className="GO-ach-reward">+{def.reward} ₽</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleMenu}>В меню</Button>
        {!isMatchWin && (
          <Button variant="primary" onClick={handleNextGame}>Следующая игра</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default GameOverDialog;
